// SnapDocs loan actions with a payload. Every optional API field is nullable — a blank value means
// "inherit the SnapDocs provider default" (or "all loan documents" for documentTypeIds) — so each
// must round-trip through load/save untouched rather than being dropped.
// Extracted from LoanActionTransformations.utils.js to keep that file within the max-lines budget.

import {
  documentReturnMethodOptions,
  LoanEditorActionsEnum,
  signingMethodOptions,
} from 'constants/loanActionEditor';

import { findOptionByValue } from './LoanActionSelectOption.utils';

const toDocumentTypeIds = documentTypes =>
  documentTypes?.length ? documentTypes.map(({ value }) => value) : null;

const toDocumentTypeOptions = (documentTypeIds, documentTypesOptions) =>
  (documentTypeIds ?? []).map(
    documentTypeId =>
      findOptionByValue(documentTypesOptions, documentTypeId) ?? {
        name: documentTypeId,
        value: documentTypeId,
      },
  );

const SNAPDOCS_FIELD_MAPPERS_BY_TYPE = {
  [LoanEditorActionsEnum.SEND_CLOSING_TO_SNAPDOCS]: {
    toApi: item => ({
      signingMethod: item.signingMethod?.value ?? null,
      brandingKey: item.brandingKey || null,
      documentTypeIds: toDocumentTypeIds(item.documentTypes),
    }),
    toForm: (actionItem, documentTypesOptions) => ({
      signingMethod: findOptionByValue(signingMethodOptions, actionItem.signingMethod),
      brandingKey: actionItem.brandingKey ?? null,
      documentTypes: toDocumentTypeOptions(actionItem.documentTypeIds, documentTypesOptions),
    }),
  },
  [LoanEditorActionsEnum.FULL_REDRAW_SNAPDOCS_CLOSING]: {
    toApi: item => ({
      signingMethod: item.signingMethod?.value ?? null,
      documentTypeIds: toDocumentTypeIds(item.documentTypes),
    }),
    toForm: (actionItem, documentTypesOptions) => ({
      signingMethod: findOptionByValue(signingMethodOptions, actionItem.signingMethod),
      documentTypes: toDocumentTypeOptions(actionItem.documentTypeIds, documentTypesOptions),
    }),
  },
  [LoanEditorActionsEnum.PARTIAL_REDRAW_SNAPDOCS_CLOSING]: {
    toApi: item => ({ documentTypeIds: toDocumentTypeIds(item.documentTypes) }),
    toForm: (actionItem, documentTypesOptions) => ({
      documentTypes: toDocumentTypeOptions(actionItem.documentTypeIds, documentTypesOptions),
    }),
  },
  [LoanEditorActionsEnum.CONVERT_SNAPDOCS_SIGNING_METHOD]: {
    toApi: item => ({ signingMethod: item.targetSigningMethod?.value ?? null }),
    toForm: actionItem => ({
      targetSigningMethod: findOptionByValue(signingMethodOptions, actionItem.signingMethod),
    }),
  },
  [LoanEditorActionsEnum.REQUEST_SNAPDOCS_NOTARY]: {
    toApi: item => ({
      scanbacksRequired: !!item.scanbacksRequired,
      instructions: item.instructions || null,
      documentReturnMethod: item.documentReturnMethod?.value ?? null,
    }),
    toForm: actionItem => ({
      scanbacksRequired: !!actionItem.scanbacksRequired,
      instructions: actionItem.instructions ?? null,
      documentReturnMethod: findOptionByValue(
        documentReturnMethodOptions,
        actionItem.documentReturnMethod,
      ),
    }),
  },
  [LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT]: {
    toApi: item => ({
      text: item.text ?? null,
      settlementAgentVisible: !!item.settlementAgentVisible,
      consumerVisible: !!item.consumerVisible,
      promptAtExecution: !!item.promptAtExecution,
    }),
    toForm: actionItem => ({
      text: actionItem.text ?? null,
      settlementAgentVisible: !!actionItem.settlementAgentVisible,
      consumerVisible: !!actionItem.consumerVisible,
      promptAtExecution: !!actionItem.promptAtExecution,
    }),
  },
  [LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING]: {
    toApi: item => ({
      reason: item.reason || null,
      promptAtExecution: !!item.promptAtExecution,
    }),
    toForm: actionItem => ({
      reason: actionItem.reason ?? null,
      promptAtExecution: !!actionItem.promptAtExecution,
    }),
  },
};

export const SNAPDOCS_PAYLOAD_ACTION_TYPES = Object.keys(SNAPDOCS_FIELD_MAPPERS_BY_TYPE);

export const getSnapdocsActionApiFormat = (formItem, common) => ({
  ...common,
  ...SNAPDOCS_FIELD_MAPPERS_BY_TYPE[common.type].toApi(formItem.snapdocsClosing ?? {}),
});

export const getSnapdocsActionFormFormat = (
  actionItem,
  itemType,
  timedSequenceOptions,
  { documentTypesOptions = [] } = {},
) => ({
  itemType,
  timedSequenceOptions,
  snapdocsClosing: SNAPDOCS_FIELD_MAPPERS_BY_TYPE[actionItem.type].toForm(
    actionItem,
    documentTypesOptions,
  ),
});
